import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Save } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CHAPTERS = [
  {
    n: "01",
    title: "The Gathering",
    body: "When the city sleeps, the colony wakes. We gather where the concrete sweats and the pipes hum. No names at the gate, only codes. Find the green glow on Park Street and follow it down.",
  },
  {
    n: "02",
    title: "Dress Code",
    body: "Rat. Rat. Rat. Ears encouraged, tails applauded, whiskers welcome. All black if in doubt. Anyone arriving in a cat costume will be turned around at the gate, politely but firmly.",
  },
  {
    n: "03",
    title: "The Ratonemy",
    body: "The colony governs itself. Respect every rat you meet in the pit, real or inflatable. Phones stay pocketed, the bass does the talking, and what happens underground stays underground.",
  },
];

export const Manifesto = () => {
  const [chapters, setChapters] = useState(DEFAULT_CHAPTERS);
  const [editing, setEditing] = useState(false);
  const drafts = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/content/manifesto`)
      .then((res) => {
        if (res.data.chapters) {
          setChapters((chs) => chs.map((c, i) => ({ ...c, ...res.data.chapters[i] })));
        }
      })
      .catch(() => {});
  }, []);

  // owner edit mode is broadcast globally (press E anywhere)
  useEffect(() => {
    const onEdit = (e) => {
      drafts.current = null;
      setEditing(e.detail.editing);
    };
    window.addEventListener("rotu:edit", onEdit);
    return () => window.removeEventListener("rotu:edit", onEdit);
  }, []);

  const onEdit = (i, field, el) => {
    if (!drafts.current) drafts.current = chapters.map((c) => ({ title: c.title, body: c.body }));
    drafts.current[i][field] = el.innerText.trim();
  };

  const save = async () => {
    const payload = drafts.current || chapters.map((c) => ({ title: c.title, body: c.body }));
    try {
      await axios.post(`${API}/content/manifesto`, { chapters: payload });
      setChapters((chs) => chs.map((c, i) => ({ ...c, ...payload[i] })));
      drafts.current = null;
      setEditing(false);
      toast.success("Ratty Rulez updated for everyone.");
    } catch (e) {
      toast.error("Save failed. The sewers ate it.");
    }
  };

  return (
    <section className="relative z-10 px-6 sm:px-12 lg:px-24 py-28 sm:py-40" data-testid="manifesto-section">
      <motion.p
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="text-[11px] tracking-[0.5em] text-[#FF0055] uppercase mb-4"
      >
        Ratty Rulez
      </motion.p>
      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="font-display text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-white border-l-4 border-[#FF0055] pl-5 mb-20"
      >
        Rules of the colony
      </motion.h2>

      {editing && (
        <div
          className="fixed top-20 right-5 z-50 border border-[#FFE600] bg-[#08080C]/95 backdrop-blur-xl px-4 py-3 flex items-center gap-4"
          data-testid="manifesto-edit-bar"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">
            Edit mode - click any box
          </span>
          <button
            onClick={save}
            data-testid="manifesto-save"
            className="flex items-center gap-2 px-4 py-2 bg-[#39FF14] text-[#08080C] font-display text-[10px] font-bold uppercase tracking-widest hover:bg-white transition-colors duration-200"
          >
            <Save size={12} /> Save
          </button>
        </div>
      )}

      <div className="space-y-24">
        {chapters.map((ch, i) => (
          <motion.div
            key={ch.n}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.05 * i }}
            className={`relative flex flex-col ${i % 2 ? "lg:items-end" : ""}`}
            data-testid={`manifesto-chapter-${ch.n}`}
          >
            <span className="font-display absolute -top-14 text-[7rem] sm:text-[10rem] font-black text-[#39FF14]/10 select-none leading-none">
              {ch.n}
            </span>
            <div className="relative max-w-xl space-y-4">
              <div
                className="border border-[rgba(0,240,255,0.35)] bg-[#111118]/80 backdrop-blur-xl px-6 py-4"
                data-testid={`manifesto-title-${ch.n}`}
              >
                <h3 className="font-bebas text-3xl sm:text-4xl tracking-wide text-white">
                  <span className="text-[#00F0FF] mr-3">{ch.n}.</span>
                  <span
                    contentEditable={editing}
                    suppressContentEditableWarning
                    onInput={(e) => onEdit(i, "title", e.currentTarget)}
                    className={editing ? "editable-box" : ""}
                    data-testid={`manifesto-title-text-${ch.n}`}
                  >
                    {ch.title}
                  </span>
                </h3>
              </div>
              <div
                className="border border-white/10 bg-[#08080C]/70 backdrop-blur-xl p-6"
                data-testid={`manifesto-body-${ch.n}`}
              >
                <p
                  contentEditable={editing}
                  suppressContentEditableWarning
                  onInput={(e) => onEdit(i, "body", e.currentTarget)}
                  className={`text-sm sm:text-base text-zinc-400 leading-relaxed ${editing ? "editable-box" : ""}`}
                  data-testid={`manifesto-body-text-${ch.n}`}
                >
                  {ch.body}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
